import {
  Contact,
  AddFromCrmResult,
  PromoteFromEventResult,
  PromotePreviewResult,
} from "@/types/Contact";
import { apiDelete, apiGet, apiPost, apiPut } from "../_apiShim";
import { DedupePreviewResponse } from "./GuestClient";
import { ConditionGroup } from "@/types/EventRule";

// Company scope comes from the JWT — companyId is NEVER part of the path
// (EventTemplateResource pattern; see EventTemplateClient.ts).
const BASE_PATH = "/api/v1/contacts/";

/** List/count filter + paging params for the contact pool. */
export interface GetContactsParams {
  page?: number;
  size?: number;
  searchTerm?: string;
  /** Only contacts with an eventHistory entry for this event. */
  eventId?: string;
  /** Only contacts created by this import batch. */
  importId?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

function buildContactQuery(params: GetContactsParams): string {
  const query = new URLSearchParams();
  if (params.page !== undefined) query.set("page", params.page.toString());
  if (params.size !== undefined) query.set("size", params.size.toString());
  if (params.searchTerm && params.searchTerm.trim() !== "")
    query.set("searchTerm", params.searchTerm);
  if (params.eventId) query.set("eventId", params.eventId);
  if (params.importId) query.set("importId", params.importId);
  if (params.sortBy) query.set("sortBy", params.sortBy);
  if (params.sortOrder) query.set("sortOrder", params.sortOrder);
  const qs = query.toString();
  return qs ? `?${qs}` : "";
}

// Get paginated contacts for the caller's company (JWT-scoped)
export async function getContacts(
  params: GetContactsParams = {},
): Promise<Contact[]> {
  return await apiGet<Contact[]>(
    `${BASE_PATH}${buildContactQuery(params)}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

/**
 * Both contact count endpoints respond `{count: n}` (backend wraps via
 * Map.of("count", ...)), unlike the guest count endpoints' bare number.
 * Unwrap tolerantly — a bare-number response (mocks, future backend
 * normalization) passes through — so the callers' `number` contract is
 * honest at runtime, not just in the cast. Rendering the raw object was
 * exactly the "Objects are not valid as a React child ({count})" crash in
 * the CRM pane.
 */
function unwrapCount(raw: unknown): number {
  if (typeof raw === "number") return raw;
  if (raw && typeof raw === "object" && "count" in raw) {
    const n = (raw as { count: unknown }).count;
    if (typeof n === "number") return n;
  }
  return 0;
}

// Count contacts matching the same filters as getContacts (paging/sort ignored)
export async function getContactsCount(
  params: Pick<GetContactsParams, "searchTerm" | "eventId" | "importId"> = {},
): Promise<number> {
  const raw = await apiGet<unknown>(
    `${BASE_PATH}count${buildContactQuery(params)}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
  return unwrapCount(raw);
}

// Get a contact by ID — detail shape incl. event history with $lookup'd
// event names/starts (ContactEventHistoryEntry.eventName/eventStart).
export async function getContact(contactId: string): Promise<Contact> {
  return await apiGet<Contact>(
    `${BASE_PATH}${contactId}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

// Create a contact manually (OWNER/ADMIN). Returns the new contactId.
// 409 `contactEmailExists` on a duplicate email within the company.
export async function createContact(contact: Contact): Promise<string> {
  return await apiPost<string>(BASE_PATH, contact, {
    "Cache-Control": "no-store",
  });
}

// Update a contact (OWNER/ADMIN). 409 `contactEmailExists` on a duplicate email.
export async function updateContact(
  contactId: string,
  contact: Partial<Contact>,
): Promise<void> {
  await apiPut<void>(`${BASE_PATH}${contactId}`, contact, {
    "Cache-Control": "no-store",
  });
}

// Delete a contact (OWNER/ADMIN). Backend cascades a $unset of
// guests.contactId backlinks.
export async function deleteContact(contactId: string): Promise<void> {
  await apiDelete<void>(`${BASE_PATH}${contactId}`, {
    "Cache-Control": "no-store",
  });
}

// Bulk delete contacts by id (OWNER/ADMIN). Same backlink cascade as the
// single delete. The body key is `ids` — what
// `ContactValidation.BulkDeleteContacts` binds; the `contactIds` that stood
// here was never read, so every delete from the CRM table came back 400
// "No valid contact IDs provided" (PB-UCRM-1). At most 1000 per call
// (`ContactService.deleteContacts` answers `tooManyIds` above that).
export async function bulkDeleteContacts(
  contactIds: string[],
): Promise<void> {
  await apiPost<void>(
    `${BASE_PATH}bulk-delete`,
    { ids: contactIds },
    {
      "Cache-Control": "no-store",
    },
  );
}

/**
 * Preview which incoming contact-import rows collide with an existing contact
 * (or an earlier row of the same file) under a `matches_new` dedupe rule.
 * Read-only — no contacts are created. Same envelope as the guest preview
 * (see {@link DedupePreviewResponse}); contact matches additionally carry
 * `matchedContactId` and `keepUnavailable` (KEEP remapped to SKIP when the
 * emails are equal — the unique email index forbids keeping both).
 */
export async function previewContactImportDuplicates(
  conditions: unknown,
  rows: Record<string, unknown>[],
): Promise<DedupePreviewResponse> {
  return await apiPost<DedupePreviewResponse>(
    `${BASE_PATH}import/dedupe-preview`,
    { conditions, rows },
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

/**
 * Copy contacts into an event's guest list ("Add from CRM"). O/A/E. Created
 * guests carry `contactId` backlinks + `origin: CRM`; the response reports
 * what was created / skipped / which field values could not be carried over.
 */
export async function addContactsToEvent(
  eventId: string,
  body: {
    contactIds: string[];
    skipAlreadyInEvent?: boolean;
  },
): Promise<AddFromCrmResult> {
  return await apiPost<AddFromCrmResult>(
    `${BASE_PATH}add-to-event/${eventId}`,
    body,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

/**
 * Rich contact-pool query body (POST variant of the list/count GETs). Kept
 * separate from {@link GetContactsParams} on purpose: the GET list endpoint
 * stays a plain param list, while the query endpoints add nested
 * `conditions` (ConditionGroup wire JSON) + `notInEventId`, which don't fit
 * query-string transport.
 */
export interface QueryContactsBody {
  page?: number;
  /** Backend caps at 200 per page. */
  size?: number;
  searchTerm?: string;
  /** Only contacts created by this import batch. */
  importId?: string;
  /** Only contacts with an eventHistory entry for this event. */
  eventId?: string;
  /** Only contacts WITHOUT an eventHistory entry for this event (diff hide). */
  notInEventId?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  /** Nested condition tree — composed server-side as a separate `$and` operand. */
  conditions?: ConditionGroup;
}

/** {@link QueryContactsBody} minus paging/sort — count + resolve variants. */
export type QueryContactsFilterBody = Omit<
  QueryContactsBody,
  "page" | "size" | "sortBy" | "sortOrder"
>;

// Query the contact pool with conditions/notInEventId support (O/A/E).
export async function queryContacts(
  body: QueryContactsBody,
): Promise<Contact[]> {
  return await apiPost<Contact[]>(
    `${BASE_PATH}query`,
    body,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

// Count contacts matching a query body (paging/sort ignored server-side).
export async function queryContactsCount(
  body: QueryContactsFilterBody,
): Promise<number> {
  const raw = await apiPost<unknown>(
    `${BASE_PATH}query/count`,
    body,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
  return unwrapCount(raw);
}

/**
 * Response of POST /contacts/resolve-ids: every matching contactId (hex) up
 * to the server cap (5000) — `truncated` is set when `total` exceeded it.
 */
export interface ResolveContactIdsResult {
  contactIds: string[];
  total: number;
  truncated: boolean;
}

// Resolve the full id set matching a query (all-matching bulk ops). O/A/E.
export async function resolveContactIds(
  body: QueryContactsFilterBody,
): Promise<ResolveContactIdsResult> {
  return await apiPost<ResolveContactIdsResult>(
    `${BASE_PATH}resolve-ids`,
    body,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

/** One normalized-email → contactId match of POST /contacts/match-by-emails. */
export interface ContactEmailMatch {
  /** Normalized (lowercased/trimmed) email as stored on the contact. */
  email: string;
  contactId: string;
}

// Match pool contacts by email (guest-side diff badges). O/A/E. ≤200 emails.
export async function matchContactsByEmails(
  emails: string[],
): Promise<{ matches: ContactEmailMatch[] }> {
  return await apiPost<{ matches: ContactEmailMatch[] }>(
    `${BASE_PATH}match-by-emails`,
    { emails },
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

/**
 * Promote event guests into the company contact pool (event→CRM transfer).
 * Company OWNER/ADMIN only — the backend re-checks. ≤1000 guestIds per call;
 * chunk larger transfers client-side.
 */
export async function promoteContactsFromEvent(
  eventId: string,
  body: {
    guestIds: string[];
    /** Opaque merge-group tokens the user chose to keep as separate contacts. */
    forceSplitKeys?: string[];
  },
): Promise<PromoteFromEventResult> {
  return await apiPost<PromoteFromEventResult>(
    `${BASE_PATH}promote-from-event/${eventId}`,
    body,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

/**
 * Read-only collapse preview of the promote above ("100 guests → 1
 * contact"). Company OWNER/ADMIN only — the backend re-checks. ≤5000
 * guestIds (the whole multi-chunk transfer previews in ONE call).
 */
export async function previewPromoteFromEvent(
  eventId: string,
  body: {
    guestIds: string[];
  },
): Promise<PromotePreviewResult> {
  return await apiPost<PromotePreviewResult>(
    `${BASE_PATH}promote-from-event/${eventId}/preview`,
    body,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}
